import { supabase } from '../lib/supabase'

/**
 * Columnas necesarias para las vistas de lista y tablero (tabla, kanban y sus
 * KPIs). Evita traer `select('*')` y reduce el payload de las consultas que
 * devuelven muchos tickets. La vista de detalle sí usa todas las columnas.
 * @type {string}
 */
export const TICKET_LIST_COLUMNS =
  'id,title,description,systemOrigin,type,statusId,priorityId,responsibleId,userId,queueId,completed,createdAt,dueAt,resolvedAt,closedAt,deleted'

/**
 * Service for managing tickets in the Supabase database.
 * @namespace ticketService
 */
const ticketService = {
  /** @type {string} Supabase schema name */
  schemaName: 'Tickets',

  /** @type {string} Table name within the schema */
  tableName: 'ticket',

  /**
   * Retrieves all tickets.
   * @returns {Promise<{success: boolean, data: Array|null, error: string|undefined}>}
   */
  async getAll() {
    try {
      const { data, error } = await supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .select(TICKET_LIST_COLUMNS)
        .eq('deleted', false)
      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error fetching tickets:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Tickets creados por un usuario (autor), sin importar quién sea el
   * responsable. Ojo: `userId` guarda el auth id del creador, mientras que
   * `responsibleId` guarda el id de `Empleados`.
   * @param {string} authUserId - `auth.uid` del autor.
   * @param {Object} [options]
   * @param {number} [options.limit] - Sólo los N más recientes.
   * @returns {Promise<{success: boolean, data: Array|null, error: string|undefined}>}
   */
  async getByAuthor(authUserId, { limit } = {}) {
    try {
      let query = supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .select(TICKET_LIST_COLUMNS)
        .eq('userId', authUserId)
        .eq('deleted', false)
        .order('createdAt', { ascending: false })
      if (limit) query = query.limit(limit)

      const { data, error } = await query
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching tickets by author:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Tiempos reales de atención de todos los tickets (no eliminados), para
   * autocalcular la política de SLA.
   * @returns {Promise<{success: boolean, data: Array|null, error: string|undefined}>}
   */
  async getSlaHistory() {
    try {
      const { data, error } = await supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .select('type,priorityId,createdAt,firstResponseAt,resolvedAt')
        .eq('deleted', false)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching SLA history:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Envía un ticket a otra cola de atención (RPC `transfer_ticket_to_queue`).
   * La base valida la cola, opcionalmente reasigna según la estrategia de la
   * cola destino, registra el historial y notifica al nuevo responsable.
   * @param {number|string} ticketId
   * @param {number} queueId - Cola destino.
   * @param {boolean} [reassign=true] - Reasignar dentro de la cola destino.
   * @returns {Promise<{success: boolean, data: {queueId: number, responsibleId: string|null, reassigned: boolean}|null, error: string|undefined}>}
   */
  async transferToQueue(ticketId, queueId, reassign = true) {
    try {
      const { data, error } = await supabase
        .schema(this.schemaName)
        .rpc('transfer_ticket_to_queue', {
          p_ticket_id: Number(ticketId),
          p_queue_id: Number(queueId),
          p_reassign: reassign,
        })
      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error transferring ticket:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Búsqueda rápida por ID (si el término es numérico) o por título.
   * @param {string} term - Texto a buscar.
   * @param {number} [limit=10] - Máximo de resultados.
   * @returns {Promise<{success: boolean, data: Array|null, error: string|undefined}>}
   */
  async search(term, limit = 10) {
    try {
      const value = String(term || '').trim()
      if (!value) return { success: true, data: [] }

      let query = supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .select('id,title,completed')
        .eq('deleted', false)
      query = Number.isNaN(Number(value))
        ? query.ilike('title', `%${value}%`)
        : query.eq('id', Number(value))

      const { data, error } = await query.limit(limit)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error searching tickets:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Retrieves a single ticket by ID.
   * @param {number|string} id - The ticket ID.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async getById(id) {
    try {
      const { data, error } = await supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .eq('deleted', false)
        .single()
      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error fetching ticket:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Creates a new ticket.
   * @param {Object} ticket - The ticket data to insert.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async create(ticket) {
    try {
      const { data, error } = await supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .insert([ticket])
        .select()
        .single()
      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error creating ticket:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Updates an existing ticket by ID.
   * @param {number|string} id - The ticket ID.
   * @param {Object} updates - Fields to update.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async update(id, updates) {
    try {
      const { data, error } = await supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error updating ticket:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Performs a logical (soft) delete by marking the ticket as deleted.
   * The record remains in the database but is excluded from listings.
   * @param {number|string} id - The ticket ID.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async softDelete(id) {
    return this.update(id, { deleted: true })
  },

  /**
   * Restores a logically deleted ticket.
   * @param {number|string} id - The ticket ID.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async restore(id) {
    return this.update(id, { deleted: false })
  },

  /**
   * Permanently deletes a ticket by ID (hard delete).
   * Prefer {@link softDelete} for normal user-facing deletion.
   * @param {number|string} id - The ticket ID.
   * @returns {Promise<{success: boolean, error: string|undefined}>}
   */
  async delete(id) {
    try {
      const { error } = await supabase
        .schema(this.schemaName)
        .from(this.tableName)
        .delete()
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting ticket:', error)
      return { success: false, error: error.message }
    }
  },
}
export default ticketService
